import React, { useState } from "react";
import { Button, Card, Col, Container, ProgressBar, Row } from "react-bootstrap";

import {
    IBillingInfo,
    IConversion,
    ILocationInfo,
    IMedicalQuestionData,
    IPatientInfo,
    IPaymentInfo,
    MEDICAL_QUESTIONS,
} from "../util/utils";
import RemoteCareDemoSettingsModal from "./RemoteCareDemoSettingsModal";
import BillingInfoStep from "./steps/BillingInfoStep";
import LocationStep from "./steps/LocationStep";
import MedicalQuestionsStep from "./steps/MedicalQuestionsStep";
import PatientInfoStep from "./steps/PatientInfoStep";
import PaymentInfoStep from "./steps/PaymentInfoStep";
import ReviewAndSubmitStep from "./steps/ReviewAndSubmitStep";
import SelfSchedulingStep from "./steps/SelfSchedulingStep";
import WelcomeStep from "./steps/WelcomeStep";

export interface IFormData {
    locationInfo: ILocationInfo;
    patientInfo: IPatientInfo;
    billingInfo: IBillingInfo;
    paymentInfo: IPaymentInfo;
    prefillFields: IMedicalQuestionData;
    selectedServiceIds: number[];
}

const DEFAULT_FORM_DATA: IFormData = {
    locationInfo: {
        address: "",
        city: "",
        state: "",
        zip: "",
    },
    billingInfo: {
        billingAddress: "",
        billingCity: "",
        billingState: "",
        billingZip: "",
        sameAsPersonal: false,
    },
    patientInfo: {
        firstName: "",
        lastName: "",
        dateOfBirth: "",
        email: "",
        phone: "",
        address: "",
        city: "",
        state: "",
        zip: "",
    },
    prefillFields: {},
    paymentInfo: {
        creditCardNumber: "",
        expirationDate: "",
        cvv: "",
    },
    selectedServiceIds: [],
};

// Total number of steps in the workflow
const TOTAL_STEPS = 6;

const RemoteCareDemo: React.FC = () => {
    const [currentStep, setCurrentStep] = useState(0);
    const [formData, setFormData] = useState<IFormData>(DEFAULT_FORM_DATA);
    const [conversion, setConversion] = useState<IConversion | null>(null);

    const [showSettings, setShowSettings] = useState(false);

    const updateFormData = <K extends keyof IFormData>(
        section: K,
        data: Partial<IFormData[K]>,
        replace?: boolean,
    ) => {
        setFormData((prev) => {
            if (Array.isArray(prev[section])) {
                return {
                    ...prev,
                    [section]: data,
                };
            }
            return {
                ...prev,
                [section]: replace
                    ? data
                    : {
                          ...(prev[section] as object),
                          ...data,
                      },
            };
        });
    };

    const handleNext = () => {
        setCurrentStep(Math.min(currentStep + 1, TOTAL_STEPS));
    };

    const handleBack = () => {
        setCurrentStep(Math.max(currentStep - 1, 0));
    };

    const scheduleNow = () => {
        setCurrentStep(7);
    };

    // Reset the entire workflow
    const handleReset = () => {
        setCurrentStep(0);
        setFormData(DEFAULT_FORM_DATA);
        setConversion(null);
    };

    const renderStep = () => {
        switch (currentStep) {
            case 0:
                return <WelcomeStep onContinue={handleNext} />;
            case 1:
                return (
                    <LocationStep
                        selectedServiceIds={formData.selectedServiceIds}
                        updateSelectedServiceIds={(data) =>
                            updateFormData("selectedServiceIds", data)
                        }
                        locationInfo={formData.locationInfo}
                        updateLocationInfo={(data) => updateFormData("locationInfo", data)}
                        conversion={conversion}
                        updateConversion={setConversion}
                        onContinue={handleNext}
                        onBack={handleBack}
                        scheduleAppointment={scheduleNow}
                    />
                );
            case 2:
                return (
                    <PatientInfoStep
                        locationInfo={formData.locationInfo}
                        patientInfo={formData.patientInfo}
                        updatePatientInfo={(data) => updateFormData("patientInfo", data)}
                        onContinue={handleNext}
                        onBack={handleBack}
                    />
                );
            case 3:
                return (
                    <MedicalQuestionsStep
                        questions={MEDICAL_QUESTIONS}
                        prefillFields={formData.prefillFields}
                        updatePrefillFields={(data) => updateFormData("prefillFields", data, true)}
                        onContinue={handleNext}
                        onBack={handleBack}
                    />
                );
            case 4:
                return (
                    <BillingInfoStep
                        patientInfo={formData.patientInfo}
                        billingInfo={formData.billingInfo}
                        updateBillingInfo={(data) => updateFormData("billingInfo", data)}
                        onContinue={handleNext}
                        onBack={handleBack}
                    />
                );

            case 5:
                return (
                    <PaymentInfoStep
                        paymentInfo={formData.paymentInfo}
                        updatePaymentInfo={(data) => updateFormData("paymentInfo", data)}
                        onContinue={handleNext}
                        onBack={handleBack}
                    />
                );
            case 6:
                return (
                    <>
                        {conversion && (
                            <ReviewAndSubmitStep
                                formData={formData}
                                conversion={conversion}
                                onComplete={handleReset}
                                onBack={handleBack}
                            />
                        )}
                    </>
                );
            case 7:
                return <>{conversion && <SelfSchedulingStep conversion={conversion} />}</>;
            default:
                return <WelcomeStep onContinue={handleNext} />;
        }
    };

    const progress = (currentStep / TOTAL_STEPS) * 100;

    return (
        <div className="telehealth-demo">
            <header className="telehealth-header">
                <Container>
                    <div className="d-flex justify-content-between align-items-center py-3">
                        <img
                            src="https://gamedaymenshealth.com/wp-content/uploads/2024/09/GD-Header-black.png"
                            alt="Lobbie Remote Care"
                            height={50}
                            className="logo"
                        />
                        <Button
                            variant="outline-secondary"
                            size="sm"
                            onClick={() => setShowSettings(true)}
                            className="settings-button"
                        >
                            <i className="bi bi-gear-fill me-1"></i> Settings
                        </Button>
                    </div>
                    {currentStep !== 7 && (
                        <div className="progress-container">
                            <ProgressBar now={progress} className="mb-4" />
                            <div className="text-end small mb-2">
                                Step {currentStep} of {TOTAL_STEPS}
                            </div>
                        </div>
                    )}
                </Container>
            </header>

            <Container className="main-content">
                <Row className="justify-content-center">
                    <Col md={10} lg={8}>
                        <Card className="main-card shadow">
                            <Card.Body>{renderStep()}</Card.Body>
                        </Card>
                    </Col>
                </Row>
            </Container>

            <footer className="footer">
                <Container>
                    <p className="text-center text-muted">
                        © {new Date().getFullYear()} Lobbie Inc. All rights reserved.
                    </p>
                </Container>
            </footer>

            <RemoteCareDemoSettingsModal
                show={showSettings}
                onHide={() => setShowSettings(false)}
            />
        </div>
    );
};

export default RemoteCareDemo;
